import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppLayout from './components/AppLayout';
import Login from './pages/Login';
import LiveTracking from './pages/LiveTracking';
import EventDashboard from './pages/EventDashboard';
import DelegateDashboard from './pages/DelegateDashboard';
import ReaderDashboard from './pages/ReaderDashboard';
import CategoryMaster from './pages/CategoryMaster';
import ConferenceLocationMaster from './pages/ConferenceLocationMaster';
import ReaderManager from './pages/ReaderManager';
import HandheldSimulator from './pages/HandheldSimulator';
import ConferenceReportMaster from './pages/ConferenceReportMaster';
import RawInventory from './pages/RawInventory';
import UserManagement from './pages/UserManagement';
import ReaderAPI from './pages/ReaderAPI';
import ClientAPIPush from './pages/ClientAPIPush';
import WelcomeDisplay from './pages/WelcomeDisplay';
import WelcomeScreenConfig from './pages/WelcomeScreenConfig';
import DatabaseConfig from './pages/DatabaseConfig';
import Analytics from './pages/Analytics';
import DelegateJourney from './pages/DelegateJourney';
import EngagementLeaderboard from './pages/EngagementLeaderboard';
import NetworkingInsights from './pages/NetworkingInsights';
import SmartInsights from './pages/SmartInsights';
import LiveHeatmap from './pages/LiveHeatmap';
import AnomalyCenter from './pages/AnomalyCenter';
import VipWatch from './pages/VipWatch';
import ReaderTelemetry from './pages/ReaderTelemetry';
import SignalQuality from './pages/SignalQuality';
import LiveReadStream from './pages/LiveReadStream';
import CoverageAudit from './pages/CoverageAudit';
import TagProvisioning from './pages/TagProvisioning';

const Loader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" />
  </div>
);

const ProtectedRoute = ({ children, adminOnly }) => {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  return children;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Routes><Route path="*" element={<Login />} /></Routes>;

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />

      {/* Full-screen display — no sidebar */}
      <Route path="/welcome-display" element={<ProtectedRoute><WelcomeDisplay /></ProtectedRoute>} />

      {/* Main app with sidebar layout */}
      <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<LiveTracking />} />

        {/* Conference Tracking */}
        <Route path="live-tracking"    element={<LiveTracking />} />
        <Route path="events"           element={<EventDashboard />} />
        <Route path="delegates"        element={<DelegateDashboard />} />
        <Route path="categories"       element={<CategoryMaster />} />
        <Route path="conf-locations"   element={<ConferenceLocationMaster />} />
        <Route path="reader-dashboard" element={<ReaderDashboard />} />
        <Route path="reader-manager"   element={<ReaderManager />} />
        <Route path="handheld"         element={<HandheldSimulator />} />
        <Route path="conf-reports"     element={<ConferenceReportMaster />} />
        <Route path="analytics"        element={<Analytics />} />
        <Route path="delegate-journey"             element={<DelegateJourney />} />
        <Route path="delegate-journey/:delegateId" element={<DelegateJourney />} />
        <Route path="engagement"       element={<EngagementLeaderboard />} />
        <Route path="networking"       element={<NetworkingInsights />} />
        <Route path="smart-insights"   element={<SmartInsights />} />
        <Route path="live-heatmap"     element={<LiveHeatmap />} />
        <Route path="anomalies"        element={<AnomalyCenter />} />
        <Route path="vip-watch"        element={<VipWatch />} />
        <Route path="reader-telemetry" element={<ReaderTelemetry />} />
        <Route path="signal-quality"   element={<SignalQuality />} />
        <Route path="live-stream"      element={<LiveReadStream />} />
        <Route path="coverage-audit"   element={<CoverageAudit />} />
        <Route path="tag-provisioning" element={<TagProvisioning />} />
        <Route path="raw-inventory"    element={<RawInventory />} />

        {/* API Integrations */}
        <Route path="reader-api"   element={<ReaderAPI />} />
        <Route path="client-push"  element={<ClientAPIPush />} />

        {/* Welcome Screen config (display is full-screen outside layout) */}
        <Route path="welcome-config" element={<WelcomeScreenConfig />} />

        {/* Admin only */}
        <Route path="db-config" element={<ProtectedRoute adminOnly><DatabaseConfig /></ProtectedRoute>} />
        <Route path="users"     element={<ProtectedRoute adminOnly><UserManagement /></ProtectedRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
